import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import SEO from '../../components/SEO';
import PublicHeader from '../../components/PublicHeader';
import PublicFooter from '../../components/PublicFooter';
import './PublicPages.css';

export default function RandomRestaurantPickerPage() {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="public-page">
      <SEO
        title="Random Restaurant Picker — Better Than a Wheel Spinner | Hangrily"
        description="Need a random restaurant picker? Hangrily is smarter than wheel spinners — it filters by cuisine, price, mood & location, then lets you swipe through real options with photos & ratings. Works for couples and groups too. Free to try."
        path="/random-restaurant-picker"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Random Restaurant Picker - Hangrily',
            description: 'A smarter random restaurant picker that uses preferences and collaborative swiping instead of pure randomness.',
            url: 'https://hangrily.app/random-restaurant-picker'
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://hangrily.app' },
              { '@type': 'ListItem', position: 2, name: 'Random Restaurant Picker', item: 'https://hangrily.app/random-restaurant-picker' }
            ]
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: [
              {
                '@type': 'Question',
                name: 'What is the best random restaurant picker?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily is the best restaurant picker because it goes beyond random selection. It uses your actual preferences — cuisine, price, distance, and mood — to curate options. Then you swipe through them Tinder-style. For couples and groups, everyone swipes and the app finds mutual favorites.'
                }
              },
              {
                '@type': 'Question',
                name: 'How does Hangrily compare to ChooseMyFood and other wheel spinners?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Wheel spinners like ChooseMyFood randomly select from a list of restaurants. Hangrily is smarter: it filters by your preferences, shows real photos and ratings, and supports collaborative decision-making with Couple Mode and Group Mode. Instead of random chance, you get a restaurant everyone actually wants to eat at.'
                }
              },
              {
                '@type': 'Question',
                name: 'Can I use a restaurant picker with my partner?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: "Absolutely! Hangrily's Couple Mode lets both partners swipe on restaurants independently. You only see the results when both finish — and you only match on places you both liked. It ends the \"where should we eat\" argument for good."
                }
              },
              {
                '@type': 'Question',
                name: 'Does the restaurant picker work in my city?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Yes! Hangrily uses Google Places data and works in any city worldwide. Just allow location access and it finds restaurants near you — whether you\'re in New York, a small town, or traveling abroad.'
                }
              },
              {
                '@type': 'Question',
                name: 'Is Hangrily free?',
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: 'Hangrily offers a free trial so you can try it out risk-free. After that, it\'s $4.99/month for unlimited sessions — a fraction of what you\'d spend on one delivery order.'
                }
              }
            ]
          }
        ]}
      />
      <PublicHeader />

      <main className="page-content">
        <section className="page-hero">
          <h1>Random Restaurant Picker — But Smarter</h1>
          <p className="hero-subtitle">
            Why leave dinner to chance when you can swipe your way to the perfect meal?
          </p>
        </section>

        <section className="content-section">
          <div className="two-column-layout">
            <div className="column-text">
              <h2>The Problem With Random Restaurant Pickers</h2>
              <p>
                Traditional random restaurant pickers — like wheel spinners and "pick for me" buttons — 
                treat every restaurant the same. A $150 steakhouse gets the same odds as a fast food joint. 
                A place 30 minutes away is treated the same as one around the corner. <strong>Random doesn't 
                mean relevant.</strong>
              </p>
              <p>
                Even worse, random pickers don't work for groups. If you're with your partner, a friend group, 
                or your family, one random pick can't possibly satisfy everyone's preferences. Someone always 
                ends up unhappy.
              </p>
              <p>
                <strong>Hangrily solves this</strong> by combining smart filtering with collaborative swiping. 
                Set your cuisine, price range, and distance preferences. Then swipe through curated restaurant 
                cards — just like Tinder, but for food. If you're with others, everyone swipes independently 
                and you match on the restaurants you all agree on.
              </p>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
                Try It Free →
              </button>
            </div>
            <div className="column-features">
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">🎯</span>
                <h3>Preference-Based</h3>
                <p>Filter by cuisine, price, distance, and mood before you ever start swiping.</p>
              </div>
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">👆</span>
                <h3>Swipe to Decide</h3>
                <p>See real photos, ratings, and distances. Swipe right to like, left to pass.</p>
              </div>
              <div className="feature-card-mini">
                <span className="feature-emoji-lg">💑</span>
                <h3>Group Consensus</h3>
                <p>Everyone swipes. The app finds the restaurant with the most agreement.</p>
              </div>
            </div>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>Hangrily vs. Random Restaurant Pickers</h2>
          <div className="comparison-table">
            <div className="compare-row compare-header">
              <div className="compare-feature">Feature</div>
              <div className="compare-them">Random Pickers</div>
              <div className="compare-us">Hangrily</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Uses your preferences</div>
              <div className="compare-them">❌ No</div>
              <div className="compare-us">✅ Cuisine, price, mood, distance</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Real restaurant data</div>
              <div className="compare-them">⚠️ Limited</div>
              <div className="compare-us">✅ Photos, ratings, hours, reviews</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Works with groups</div>
              <div className="compare-them">❌ One random pick</div>
              <div className="compare-us">✅ Everyone swipes & matches</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Couple mode</div>
              <div className="compare-them">❌ No</div>
              <div className="compare-us">✅ Both partners swipe independently</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Location-aware</div>
              <div className="compare-them">⚠️ Basic</div>
              <div className="compare-us">✅ GPS + custom radius</div>
            </div>
            <div className="compare-row">
              <div className="compare-feature">Decision satisfaction</div>
              <div className="compare-them">😐 Hit or miss</div>
              <div className="compare-us">😍 Everyone agrees</div>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>How Hangrily's Restaurant Picker Works</h2>
          <div className="steps-grid">
            <div className="step-card">
              <span className="step-number">1</span>
              <h3>Set Your Preferences</h3>
              <p>Choose cuisine types, price range ($-$$$$), distance, and even your mood. Craving sushi? On a budget? We'll filter accordingly.</p>
            </div>
            <div className="step-card">
              <span className="step-number">2</span>
              <h3>Swipe Through Restaurants</h3>
              <p>See real restaurant cards with photos, star ratings, cuisine type, and distance. Swipe right on places that look good, left on ones that don't.</p>
            </div>
            <div className="step-card">
              <span className="step-number">3</span>
              <h3>Get Your Match</h3>
              <p>Our algorithm picks the restaurant with the most agreement. In solo mode, it's your top-swiped pick. In couple or group mode, it's the one everyone loved.</p>
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '32px' }}>
            <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
              Pick a Restaurant Now →
            </button>
          </div>
        </section>

        <section className="content-section alt-bg">
          <h2>Perfect For Every Dining Situation</h2>
          <div className="use-case-grid">
            <div className="use-case-card">
              <span className="use-case-emoji">🍽️</span>
              <h3>Solo Dining</h3>
              <p>Can't decide what to eat alone? Let the app narrow 50 options down to the perfect one in 3 quick rounds of swiping.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">💑</span>
              <h3>Date Night</h3>
              <p>No more "I don't care, you pick." Both of you swipe independently and the app finds a place you'll both love.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">👥</span>
              <h3>Friend Groups</h3>
              <p>Share a code with up to 10 friends. Everyone swipes on their own phone. The group's top pick wins — democratically.</p>
            </div>
            <div className="use-case-card">
              <span className="use-case-emoji">👨‍👩‍👧‍👦</span>
              <h3>Family Dinners</h3>
              <p>Kids want burgers, mom wants Thai, dad wants pizza. Let everyone vote and find the restaurant that makes everyone happy.</p>
            </div>
          </div>
        </section>

        <section className="content-section">
          <h2>Frequently Asked Questions About Restaurant Pickers</h2>
          <div className="faq-list">
            <details className="faq-item">
              <summary>What is the best random restaurant picker?</summary>
              <p>Hangrily is the best restaurant picker because it goes beyond random selection. It uses your actual preferences — cuisine, price, distance, and mood — to curate options. Then you swipe through them Tinder-style. For couples and groups, everyone swipes and the app finds mutual favorites.</p>
            </details>
            <details className="faq-item">
              <summary>How does Hangrily compare to ChooseMyFood and other wheel spinners?</summary>
              <p>Wheel spinners like ChooseMyFood randomly select from a list of restaurants. Hangrily is smarter: it filters by your preferences, shows real photos and ratings, and supports collaborative decision-making with Couple Mode and Group Mode. Instead of random chance, you get a restaurant everyone actually wants to eat at.</p>
            </details>
            <details className="faq-item">
              <summary>Can I use a restaurant picker with my partner?</summary>
              <p>Absolutely! Hangrily's Couple Mode lets both partners swipe on restaurants independently. You only see the results when both finish — and you only match on places you both liked. It ends the "where should we eat" argument for good.</p>
            </details>
            <details className="faq-item">
              <summary>Does the restaurant picker work in my city?</summary>
              <p>Yes! Hangrily uses Google Places data and works in any city worldwide. Just allow location access and it finds restaurants near you — whether you're in New York, a small town, or traveling abroad.</p>
            </details>
            <details className="faq-item">
              <summary>Is Hangrily free?</summary>
              <p>Hangrily offers a free trial so you can try it out risk-free. After that, it's $9.99/month for unlimited sessions — a fraction of what you'd spend on one delivery order.</p>
            </details>
          </div>
        </section>

        <section className="content-section">
          <div className="internal-links-section">
            <h2>Explore More</h2>
            <div className="internal-links-grid">
              <Link to="/where-should-i-eat" className="internal-link">Where Should I Eat?</Link>
              <Link to="/couples-restaurant-picker" className="internal-link">Couples Restaurant Picker</Link>
              <Link to="/group-restaurant-decision" className="internal-link">Group Restaurant Decision</Link>
              <Link to="/restaurant-wheel-alternative" className="internal-link">Restaurant Wheel Alternative</Link>
              <Link to="/date-night-restaurants" className="internal-link">Date Night Restaurants</Link>
              <Link to="/what-to-eat-tonight" className="internal-link">What to Eat Tonight</Link>
              <Link to="/best-restaurants-near-me" className="internal-link">Best Restaurants Near Me</Link>
              <Link to="/food-decision-fatigue" className="internal-link">Food Decision Fatigue</Link>
              <Link to="/locations" className="internal-link">Browse Cities</Link>
            </div>
          </div>
        </section>

        <section className="cta-section">
          <h2>Ready to Stop Leaving Dinner to Chance?</h2>
          <p>Join thousands of couples and friend groups who use Hangrily to decide where to eat — without the arguments.</p>
          <button className="btn btn-primary btn-lg" onClick={() => navigate('/try')}>
            Get Started Free →
          </button>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
